import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { StorageService, StrapiService, ToastService } from '@core/services';
import { AuthService, PermissionsService } from '@customer/services';
import { ContactService } from '@customer/contact';
import { Account as AccountService } from '@customer/account/account.svc';
import { BehaviorSubject } from 'rxjs';
import { SvgIconComponent } from 'angular-svg-icon';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { Account as AccountModel, LoggedInUser } from '@legacy-models/portal/Account';
import { Contact } from '@legacy-models/portal/Contact';

@Component({
  selector: 'oke-profile',
  templateUrl: './profile.html',
  styleUrl: './profile.scss',
  imports: [ReactiveFormsModule, SvgIconComponent, AsyncPipe, TitleCasePipe]
})
export class Profile implements OnInit, AfterViewInit {
  account!: AccountModel;
  user!: Contact;
  loggedInUser!: LoggedInUser;
  mobileRegex = /^(\+\d{1,3}[- ]?)?\d{10}$/;
  acctEsaaStatusText = 'No';
  accountAdministrators: Pick<Contact, 'Email' | 'FullName'>[] = [];
  isNoAccountAdministrators = false;
  isAccountOnlyNGP = false;
  isLoading = false;
  isEsaaSignedValue = false;
  isContactFormEditable = false;
  isAccoountFormEditable = false;
  isSubmitting = false;
  feedbackMsgCounter = 1;
  isCusUser = false;
  hasContactDetailsBeenSaved = false;
  contactForm: FormGroup;
  accountForm: FormGroup;
  feedbackMsgs: {
    id?: string;
    type: string;
    msg: string;
  }[] = [];
  pageContentSource = new BehaviorSubject({
    Heading: 'User &amp; Company Details'
  });
  pageContent$ = this.pageContentSource.asObservable();
  private readonly _strapiSvc = inject(StrapiService);
  private readonly _accountSvc = inject(AccountService);
  readonly authSvc = inject(AuthService);
  readonly storageSvc = inject(StorageService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _contactSvc = inject(ContactService);
  private readonly _toastSvc = inject(ToastService);
  readonly permissionsSvc = inject(PermissionsService);

  constructor() {
    this.getPageContent();
    this.storageSvc.currentUser$.subscribe(currUser => {
      if (currUser?.Contact) {
        this.user = currUser.Contact;
      }
    });
    this.storageSvc.currentAccount$.subscribe(currAcc => {
      if (currAcc) {
        this.account = currAcc;
        if (this.account.IsEsaaSigned) {
          this.acctEsaaStatusText = 'Yes';
          this.isEsaaSignedValue = true;
        } else {
          this.acctEsaaStatusText = 'No';
          this.isEsaaSignedValue = false;
        }
        this.isAccountOnlyNGP = this._accountSvc.isAccountNGPIntastateOnly(this.account);
      }
    });

    this.storageSvc.loggedInUser$.subscribe(user => {
      if (user) {
        this.loggedInUser = user;
        this.isCusUser = this.authSvc.isCusUser(user.UserName);
      }
    });

    this.contactForm = this._formBuilder.group({
      FirstName: new FormControl(
        { value: this.user.FirstName, disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      LastName: new FormControl(
        { value: this.user.LastName, disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      Mobile: new FormControl(
        { value: this.user.Mobile, disabled: true },
        { nonNullable: true, validators: [Validators.required, Validators.pattern(this.mobileRegex)] }
      ),
      Phone: new FormControl(
        { value: this.user.Phone, disabled: true },
        { nonNullable: true, validators: [Validators.pattern(this.mobileRegex)] }
      )
    });

    if (!this.user.IsEmployee && !this.isCusUser) {
      // this.authSvc.testData();
      this.contactForm.addControl(
        'Email',
        new FormControl(
          { value: this.user.Email?.toLowerCase() ?? '', disabled: true },
          { nonNullable: true, validators: [Validators.required, Validators.email] }
        )
      );
    }
  }

  ngOnInit(): void {
    this.accountForm = this._formBuilder.group({
      esaaStatus: new FormControl({
        value: this.account.IsEsaaSigned,
        disabled: this.permissionsSvc.hasPortalAdminRole() ? false : true
      })
    });
    if (!this.isAccountOnlyNGP) {
      this.getAccountAdmins();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.storageSvc.setCurrentPage('My Profile');
    });
  }

  getPageContent(): void {
    const strapiPageProfile = JSON.parse(sessionStorage.getItem('strapiPageProfile') as string);
    if (!strapiPageProfile) {
      this._strapiSvc.getProfileContent().subscribe(content => {
        sessionStorage.setItem('strapiPageProfile', JSON.stringify(content));
        this.pageContentSource.next(content);
      });
    } else {
      this.pageContentSource.next(strapiPageProfile);
    }
  }

  isPrimaryButtonDisabled(): boolean {
    let isPrimaryButtonDisabled = false;
    if (this.isContactFormEditable) {
      isPrimaryButtonDisabled = true;
      if (this.contactForm.dirty && this.contactForm.valid) {
        isPrimaryButtonDisabled = false;
      }
    } else if (this.isSubmitting) {
      isPrimaryButtonDisabled = true;
    }
    return isPrimaryButtonDisabled;
  }

  getAccountAdmins(): void {
    this.isLoading = true;
    this._accountSvc.getAccountAdministrators(this.account.AccountId).subscribe({
      next: response => {
        this.isLoading = false;
        this.accountAdministrators = response.map((administrator: Record<'Email' | 'FullName', string>) => ({
          Email: administrator.Email,
          FullName: administrator.FullName
        }));
      },
      error: () => {
        this.isNoAccountAdministrators = true;
        this.isLoading = false;
      }
    });
  }

  handleUserPrimaryClick(key: boolean | string, clickEvt?: any): void {
    if (typeof key === 'boolean') {
      const action = !key ? 'disable' : 'enable';
      for (const formControl in this.contactForm.controls) {
        if (Object.hasOwn(this.contactForm.controls, formControl)) {
          if (formControl !== 'Email') {
            const control = this.contactForm.get(formControl)!;
            if (action === 'disable') {
              control.disable();
            } else {
              control.enable();
            }
          }
          if (action === 'disable' && !this.hasContactDetailsBeenSaved) {
            this.contactForm.get(formControl)!.reset();
          }
        }
      }
      this.isContactFormEditable = !this.isContactFormEditable;
    } else if (key === 'save') {
      this.feedbackMsgs = [];
      this.submitForm(clickEvt?.target as HTMLElement);
    }
  }

  clearFeedbackMsg(msgId: string): void {
    const clearFeedbackMsgTimeout = setTimeout(() => {
      this.feedbackMsgs.splice(
        this.feedbackMsgs.findIndex(feedbackMsg => feedbackMsg.id === msgId),
        1
      );
      window.clearTimeout(clearFeedbackMsgTimeout);
    }, 8000);
  }

  submitForm(buttonElem: HTMLElement): void {
    this.feedbackMsgs = [];
    const contactPayload: Record<string, string> = {
      ContactId: this.user.Id!,
      FirstName: this.contactForm.get('FirstName')!.value,
      LastName: this.contactForm.get('LastName')!.value,
      Phone: this.contactForm.get('Phone')!.value,
      Mobile: this.contactForm.get('Mobile')!.value,
      Email: this.user.Email!
    };
    this.isSubmitting = true;
    buttonElem.classList.add('is-waiting');
    this._contactSvc.updateContact(contactPayload).subscribe({
      next: () => {
        this.isSubmitting = false;
        buttonElem.classList.remove('is-waiting');
        this.hasContactDetailsBeenSaved = true;
        const currUser = this.storageSvc.currentUserSource.value;
        contactPayload.FullName = contactPayload.FirstName + ' ' + contactPayload.LastName;
        if (currUser?.Contact) {
          for (const [prop, value] of Object.entries(contactPayload)) {
            if (Object.hasOwn(currUser.Contact, prop)) {
              Object.assign(currUser.Contact, { [prop]: value });
            }
          }
          this.storageSvc.setCurrentUser(currUser);
        }
        for (const controlName in this.contactForm.controls) {
          if (Object.hasOwn(this.contactForm.controls, controlName)) {
            this.contactForm.get(controlName)?.setValue(contactPayload[controlName]);
          }
        }
        this.feedbackMsgs.push({ type: 'success', msg: 'Your User details were successfully updated.' });
        this.handleUserPrimaryClick(false);
      },
      error: () => {
        this.isSubmitting = false;
        buttonElem.classList.remove('is-waiting');
        const feedbackMsg = {
          id: `msg${this.feedbackMsgCounter}`,
          type: 'error',
          msg: 'There was an issue trying to update your User details.'
        };
        this.feedbackMsgs.push(feedbackMsg);
        this.clearFeedbackMsg(feedbackMsg.id);
        this.feedbackMsgCounter++;
        this.handleUserPrimaryClick(false);
      }
    });
  }

  toggleEsaaStatus(): void {
    if (this.acctEsaaStatusText === 'No') {
      this.acctEsaaStatusText = 'Yes';
      this.isEsaaSignedValue = true;

      if (this.account.IsEsaaSigned === this.isEsaaSignedValue) {
        this.isAccoountFormEditable = false;
      } else {
        this.isAccoountFormEditable = true;
      }
    } else {
      this.acctEsaaStatusText = 'No';
      this.isEsaaSignedValue = false;
      if (this.account.IsEsaaSigned === this.isEsaaSignedValue) {
        this.isAccoountFormEditable = false;
      } else {
        this.isAccoountFormEditable = true;
      }
    }
  }

  updateAccountEsaaStatus(event: MouseEvent): void {
    this.feedbackMsgs = [];
    const button = event.currentTarget as HTMLElement;
    button.classList.add('is-waiting');
    this._accountSvc
      .updateAccountEsaaFlag(this.account.AccountId, this.accountForm.controls['esaaStatus'].value)
      .subscribe(
        () => {
          button.classList.remove('is-waiting');
          this.isAccoountFormEditable = false;
          this.accountForm.markAsPristine();
          this.account.IsEsaaSigned = this.accountForm.controls['esaaStatus'].value;
          if (this.account.IsEsaaSigned) {
            this.account.EsaaStatus = 'Signed';
          } else {
            this.account.EsaaStatus = 'Grandfathered';
          }
          this.storageSvc.storeCurrentAccount(this.account);
        },
        () => {
          this._toastSvc.dismissAll();
          const attemptedChangeValue = this.accountForm.controls['esaaStatus'].value;
          button.classList.remove('is-waiting');
          this.accountForm.controls.esaaStatus.setValue(!attemptedChangeValue);
          this.acctEsaaStatusText = this.account.IsEsaaSigned ? 'Yes' : 'No';
          this.isAccoountFormEditable = false;
          this.accountForm.markAsPristine();
          const feedbackMsg = {
            id: `msg${this.feedbackMsgCounter}`,
            type: 'error',
            msg: 'There was an issue updating your accounts ESAA Signed status.'
          };
          this.feedbackMsgs.push(feedbackMsg);
        }
      );
  }

  isAccountAdministratorsVisible(): boolean {
    let isAccountAdministratorsVisible = true;
    const isNGPIntrastatueUserOnly =
      this.user.BusinessSegments?.includes('NGPIntrastate') && !this.user.BusinessSegments?.includes('NGGP');
    if (this.permissionsSvc.hasPortalEmployeeRole()) {
      return isAccountAdministratorsVisible;
    }
    if (isNGPIntrastatueUserOnly || this.isAccountOnlyNGP) {
      isAccountAdministratorsVisible = false;
    }
    return isAccountAdministratorsVisible;
  }
}
